/**
 * Patternz SHIELD Dashboard — React Component
 *
 * Installation:
 *   npm install react-leaflet leaflet
 *
 * Add to your entry file (main.jsx / index.js):
 *   import 'leaflet/dist/leaflet.css'
 *
 * Then import and render:
 *   import ShieldDashboard from './ShieldDashboard'
 *   <ShieldDashboard />
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { MapContainer, Circle, Marker, Polyline, useMap, Tooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import BaseMapLayer from './components/maps/BaseMapLayer.js';
import _ from 'lodash';

// Components
import Nav from './components/Nav.js';
import LoginScreen from './components/LoginScreen.js';
import SettingsScreen from './components/SettingsScreen.js';
import SuspectProfile from './components/SuspectProfile.js';
import SuspectPanel from './components/SuspectPanel.js';
import AddZoneModal, { type NewZonePayload } from './components/AddZoneModal.js';
import ThreatProfileModal from './components/ThreatProfileModal.js';
import AlertPanel from './components/AlertPanel.js';
import IntelTicker from './components/IntelTicker.js';
import WatchlistPanel from './components/WatchlistPanel.js';
import { MapControls, MapFilters, MapStats } from './components/MapOverlays.js';

// API service
import {
  createProfile,
  createSite,
  deleteProfile,
  deleteSite,
  fetchAppDetails,
  updatePerimeter,
  updateProfile,
  updateSite,
  updateSuspect,
} from './api/index.js';
import { useLiveData, type LoadFailure } from './hooks/useLiveData.js';
import type { ThreatEvent, ThreatState } from './domain/threatAssessment.js';
import {
  type Alert,
  type AppSettings,
  type CountryOption,
  type MapView,
  type PerimeterConfig,
  type Position,
  type ProtectedUser,
  type Site,
  type Suspect,
  type ThreatProfile,
  type TickerItem,
  type UpdatePerimeterBody,
  ZONE_TYPES,
  can,
} from './api/types.js';
import { getFlagEmoji } from './utils/Helpers.js';
import { useSettings } from './SettingsProvider.js';
import { useSession } from './session/SessionProvider.js';
import { useTranslation } from './i18n/useTranslation.js';
import { fitToSites } from './domain/geo.js';
import Spinner from './components/common/Spinner.js';

// Fix Leaflet default icon path (broken in Webpack/Vite bundles)
// delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ─────────────────────────────────────────────────────────────────────────────
// PERIMETER RENDER CONFIG
// ─────────────────────────────────────────────────────────────────────────────

/** Fallback map view when there are no sites to fit (Budapest, Jewish Quarter). */
const DEFAULT_VIEW = { lat: 47.4979, lng: 19.061, zoom: 15 };

const PERIM_CONFIG = [
  { color: '#e63946', dashArray: undefined, weight: 2, fillOpacity: 0.2 },
  { color: '#e63946', dashArray: undefined, weight: 2, fillOpacity: 0.1 },
  { color: '#f4a261', dashArray: '5 4', weight: 1.5, fillOpacity: 0.06 },
  { color: '#d4af37', dashArray: '5 4', weight: 1.5, fillOpacity: 0.03 },
];

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

function nowTime() {
  return new Date().toTimeString().slice(0, 8);
}

/** Human-readable message of a thrown value (ApiError, Error or anything else). */
function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err ?? '');
}

// ─────────────────────────────────────────────────────────────────────────────
// LEAFLET ICON FACTORIES
// ─────────────────────────────────────────────────────────────────────────────

function makeSuspectIcon(s) {
  const near = s._near ?? false;
  const label = `...${s.id.slice(s.id.length - 6)}`;
  return L.divIcon({
    className: '',
    html: `<div class="sm ${s.level}${near ? ' near' : ''}">
      <div class="sm-pulse"></div>
      <div class="sm-dot"></div>
      <span>${label}</span>
    </div>`,
    iconSize: [80, 20],
    iconAnchor: [7, 7],
  });
}

function makeProtectedUserIcon(user: ProtectedUser) {
  const label = user.userId ? `...${user.userId.slice(-6)}` : user.objectId.slice(-6);
  return L.divIcon({
    className: '',
    html: `<div class="pum">
      <div class="pum-pulse"></div>
      <div class="pum-dot"></div>
      <span>${label}</span>
    </div>`,
    iconSize: [80, 20],
    iconAnchor: [7, 7],
  });
}

function makeLocationIcon(loc) {
  const cls = loc.status === 'alert' ? 'alrt' : loc.status === 'watch' ? 'watch' : 'safe';
  const icon = ZONE_TYPES.find((t) => t.value === loc.type)?.icon;
  return L.divIcon({
    className: '',
    html: `<div class="plm ${cls}"><span>${icon}</span></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// MAP HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a flyTarget that fits all sites in `positions` with a buffer (meters).
 * Pass the result to setFlyTarget — MapController will call map.flyTo or map.flyToBounds.
 */

function MapController({ flyTarget }) {
  const map = useMap();
  useEffect(() => {
    // Invalidate size after mount to fix partial rendering
    setTimeout(() => map.invalidateSize(), 100);
  }, [map]);
  useEffect(() => {
    if (!flyTarget) return;
    if (flyTarget.bounds) {
      map.flyToBounds(flyTarget.bounds, { padding: [24, 24], duration: 1.2 });
    } else {
      map.flyTo([flyTarget.lat, flyTarget.lng], flyTarget.zoom ?? 17, { duration: 1.2 });
    }
  }, [flyTarget, map]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const PerimeterLabel = ({ center, radius, color, show }) => {
  const [currentZoom, setCurrentZoom] = useState(13);

  const map = useMapEvents({
    zoomend: () => {
      // This state update forces React to re-calculate and hide/show the label
      setCurrentZoom(map.getZoom());
    },
  });

  const isVisible = show && currentZoom >= 14; // Only show when zoomed in enough

  if (!isVisible) return;

  return (
    // Invisible marker at the top border
    <Marker position={center} icon={L.divIcon({ className: 'hidden-icon' })}>
      <Tooltip permanent direction="top" className="radius-tooltip">
        <span style={{ color, fontWeight: 'bold' }}>{radius}m</span>
      </Tooltip>
    </Marker>
  );
};

const ToastItem = memo(function ToastItem({ toast, onRemove }: { toast: any; onRemove: (id: string) => void }) {
  useEffect(() => {
    const t1 = setTimeout(() => onRemove(toast.id), 4200);
    return () => clearTimeout(t1);
  }, [toast.id, onRemove]);

  return (
    <div className={`toast ${toast.type}`}>
      <div className="toast-head">
        <div className={`toast-title ${toast.type}`}>{toast.title}</div>
        <div className="toast-head-right">
          <div className="toast-time">{nowTime()}</div>
          <div className="toast-close" onClick={() => onRemove(toast.id)}>
            ✕
          </div>
        </div>
      </div>
      <div className="toast-body">{toast.body}</div>
      <div className="toast-progress">
        <div className={`toast-progress-fill ${toast.type}`} />
      </div>
    </div>
  );
});

function ToastContainer({ toasts, onRemove }) {
  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={onRemove} />
      ))}
    </div>
  );
}

function BreachOverlay({ breach, onAck, onDismiss, onNotHostile, canMarkNotHostile = true }) {
  if (!breach) return null;
  return (
    <div className={`breach-overlay ${breach ? 'show' : ''}`}>
      <div className="breach-bg" />
      <div className="breach-card">
        <div className="bc-title">⚡ PERIMETER BREACH</div>
        <div className="bc-sub">
          {breach.suspect?.id} — {breach.suspect?.profiles?.join(', ')}
        </div>
        <div className="bc-row">
          <div className="bc-item">
            <div className="bc-label">Suspect</div>
            <div className="bc-val">{breach.suspect?.id}</div>
          </div>
        </div>
        <div className="bc-row">
          <div className="bc-item">
            <div className="bc-label">Distance</div>
            <div className="bc-val">{Math.round(breach.dist)}m</div>
          </div>
          <div className="bc-item">
            <div className="bc-label">Score</div>
            <div className="bc-val">{breach.suspect?.score}</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 14 }}>
          Immediate perimeter of <strong style={{ color: 'var(--t1)' }}>{breach.loc?.name}</strong>
        </div>
        <button className="bc-ack" onClick={onAck}>
          ACKNOWLEDGE — INCREASE MONITORING
        </button>
        <div className="bc-actions">
          <button className="bc-dismiss" onClick={onDismiss}>
            DISMISS
          </button>
          {canMarkNotHostile && (
            <button className="bc-not-hostile" onClick={onNotHostile}>
              NOT HOSTILE
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AppDetailsModal({ app, onDismiss }) {
  if (!app) return null;

  return (
    <div className={`breach-overlay ${app ? 'show' : ''}`}>
      <div className="app-details-bg" />
      <div className="app-details-card">
        <div className="app-details-title">{app.isLoading ? 'Details loading...' : `${app.name} Details`}</div>
        {!app.isLoading && (
          <React.Fragment>
            <div className="bc-row">
              <div className="app-details-item">
                <div className="bc-label">Developer</div>
                <div className="app-details-val">{app.developer}</div>
              </div>
              <div className="app-details-item">
                <div className="bc-label">Categories</div>
                <div className="app-details-val">{app.categories ? app.categories.join(', ').toString() : ''}</div>
              </div>
            </div>
            {!!app.keywords?.length && (
              <div className="bc-row">
                <div className="app-details-item">
                  <div className="bc-label">Keywords</div>
                  <div className="app-details-val">{app.keywords ? app.keywords.join(', ').toString() : ''}</div>
                </div>
              </div>
            )}
            <div className="bc-row">
              <div className="app-details-item">
                <div className="bc-label">Price</div>
                <div className="app-details-val">{app.price === 0 ? 'FREE' : app.price}</div>
              </div>
              <div className="app-details-item">
                <div className="bc-label">Score</div>
                <div className="app-details-val">{app.score}</div>
              </div>
              <div className="app-details-item">
                <div className="bc-label">OS</div>
                <div className="app-details-val">{app.os}</div>
              </div>
            </div>
            <div className="bc-row">
              <div className="app-details-item">
                <div className="bc-label">URL</div>
                <div className="app-details-val">
                  <a href={app.external_source.url} target="_blank">
                    {app.external_source.url}
                  </a>
                </div>
              </div>
            </div>
            <div className="bc-row">
              <div className="app-details-item">
                <div className="bc-label">Description</div>
                <div className="app-details-val">
                  <p className="app-details-desc">{app.description}</p>
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
        <div className="bc-actions">
          <button className="bc-dismiss" onClick={onDismiss}>
            DISMISS
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

export default function ShieldDashboard() {
  // ── Auth state ──────────────────────────────────────────────────────────────
  const {
    status: sessionStatus,
    account,
    impersonatedBy,
    stopImpersonation,
    country,
    setCountry,
    editMode,
    setEditMode,
    effectiveRole: role,
    logout: endSession,
  } = useSession();
  const loggedIn = !!account;
  const { t } = useTranslation();
  const canManageZones = can(role, 'manageZones');
  const canManageProfiles = can(role, 'manageThreatProfiles');
  const canAnnotate = can(role, 'annotateSuspects');
  // Zone and threat-profile editors live on the main page; `zone`/`profile` set = edit mode.
  const [zoneModal, setZoneModal] = useState<{ open: boolean; zone?: Site }>({ open: false });
  const [profileModal, setProfileModal] = useState<{ open: boolean; profile?: ThreatProfile }>({ open: false });
  const [activeView, setActiveView] = useState('map');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // ── Settings ────────────────────────────────────────────────────────────────
  const { settings, setSettings } = useSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── UI selection state ──────────────────────────────────────────────────────
  const [selectedSuspectId, setSelectedSuspectId] = useState<string | null>(null);
  const [selectedLoc, setSelectedLoc] = useState<Site | null>(null);
  const [flyTarget, setFlyTarget] = useState<any>(null);
  const [perimsOn, setPerimsOn] = useState(true);
  const [mapFilters, setMapFilters] = useState({
    highThreat: true,
    protected: true,
    flagged: true,
    trails: true,
    protectedUsers: true,
  });

  // ── Alerts / toasts ─────────────────────────────────────────────────────────
  const [alerts, setAlerts] = useState<Array<Alert>>([]);
  const [toasts, setToasts] = useState<any[]>([]);
  const [breach, setBreach] = useState<any>(null);
  // TODO: remove later
  const [notHostileIds, setNotHostileIds] = useState<string[]>([]);
  const [appDetails, setAppDetails] = useState<any>(null);
  const [tickerItems, setTickerItems] = useState<Array<TickerItem>>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(nowTime);
  /** Where the map opens for the current account/country (country view when there are no sites yet). */
  const [mapHome, setMapHome] = useState<MapView | null>(null);

  // ── Toast helper ─────────────────────────────────────────────────────────────
  const showToast = useCallback(
    (type: string, title: string, body: any, notificationsOverride?: boolean) => {
      if (notificationsOverride !== undefined ? !notificationsOverride : !settings?.general.toastNotifications) return;
      const id = Date.now() + Math.random();
      setToasts((t) => [...t.slice(-4), { id, type, title, body }]);
    },
    [settings?.general.toastNotifications]
  );

  const removeToast = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  // ── Proximity alert ──────────────────────────────────────────────────────────
  const triggerProximityAlert = useCallback(
    (
      id: string,
      perimeterIdx: number,
      perimetersData: PerimeterConfig['perimeters'],
      loc: Site,
      s: Suspect,
      showToastNotification?: boolean,
      panToBreach?: boolean
    ) => {
      const typeMap = { 0: 'c', 1: 'c', 2: 'w', 3: 'i' };
      const titleMap = {
        0: `⚡ ${perimetersData[0].upperBound}m BREACH`,
        1: `${perimetersData[1].upperBound}m ALERT`,
        2: 'CAUTION ZONE',
        3: 'WATCH ZONE ENTRY',
      };
      const t = typeMap[perimeterIdx] ?? 'i';
      const newAlert = {
        t,
        time: nowTime(),
        title: titleMap[perimeterIdx],
        body: `${id} entered ${perimetersData[perimeterIdx].upperBound}m perimeter of ${loc?.name ?? 'protected site'}`,
        loc: loc?.name?.split(' ').slice(0, 2).join(' '),
        sus: id,
      };
      setAlerts((prev) => [newAlert, ...prev]);
      setTotalAlerts((n) => n + 1);
      showToast(t, titleMap[perimeterIdx], newAlert.body, showToastNotification);
      setTickerItems((prev) => [...prev, { t, text: newAlert.body }]);

      if (perimeterIdx <= 0 && s.level === 'h' && loc && panToBreach) {
        const locRadius = perimetersData[3].upperBound;
        setFlyTarget(fitToSites([loc], locRadius));
        if (perimsOn) setSelectedLoc(loc);
      }
    },
    [showToast, perimsOn]
  );

  // ── Threat events → UI (alerts, toasts, ticker, breach overlay) ──────────────
  const handleThreatEvent = useCallback(
    (event: ThreatEvent, eventSettings: AppSettings) => {
      if (event.kind === 'breach') {
        if (eventSettings.alerts.perimeters.critical.triggerOverlay) {
          setTimeout(() => setBreach({ suspect: event.suspect, loc: event.site, dist: event.dist }), 400);
        }
        return;
      }
      triggerProximityAlert(
        event.suspect.id,
        event.perimeterIdx,
        event.perimeters,
        event.site,
        event.suspect,
        eventSettings.general.toastNotifications,
        eventSettings.alerts.perimeters.critical.autoPan
      );
    },
    [triggerProximityAlert]
  );

  // Live-update outages are reported once per outage (not once per 10 s poll), and once more on recovery.
  const pollFailingRef = useRef(false);

  const handleInitialLoad = useCallback(
    (loadedSettings: AppSettings, state: ThreatState, countries: Array<CountryOption>, failures: Array<LoadFailure>) => {
      pollFailingRef.current = false;
      const countryView = countries.find((c) => c.iso3 === country)?.view;
      // Home view for this load: the country when there is nothing to fit yet, else the mission center.
      const home: MapView = state.sites.length === 0 && countryView
        ? countryView
        : { lat: loadedSettings.mission.mapCenterLat, lng: loadedSettings.mission.mapCenterLng, zoom: loadedSettings.general.mapDefaultZoom };
      setMapHome(home);
      setSettings(loadedSettings);
      setConnectionError(null);
      setAlerts([]);
      setTotalAlerts(0);
      setSelectedLoc(null);
      if (loadedSettings.mission.autoCenterOnSites && state.sites.length) {
        setFlyTarget(fitToSites(state.sites) ?? home);
      } else {
        setFlyTarget(home);
      }
      // Sources that fell back to empty data: the page still renders, the user is told what is missing.
      // `settings` is still null in showToast's closure here, so the notification preference is bypassed
      // explicitly; error notices are not alert toasts anyway.
      failures.forEach(({ source, error }) => {
        console.error(`Failed to load ${source}:`, error);
        if (source === 'countries') return; // country views are a convenience only
        showToast('e', t('loading.failedTitle'), t(`loading.failed.${source}`, { message: errorMessage(error) }), true);
      });
    },
    [setSettings, country, showToast, t]
  );

  const handleLoadError = useCallback((err: unknown) => {
    console.error('Failed to load initial data:', err);
    setConnectionError(`Cannot reach the SHIELD API — ${errorMessage(err)}`);
  }, []);

  const handlePoll = useCallback(() => {
    setLastUpdate(nowTime());
    if (pollFailingRef.current) {
      pollFailingRef.current = false;
      showToast('s', t('loading.pollRecoveredTitle'), t('loading.pollRecovered'), true);
    }
  }, [showToast, t]);
  const handlePollError = useCallback(
    (err: unknown) => {
      if (pollFailingRef.current) return;
      pollFailingRef.current = true;
      showToast('e', t('loading.failedTitle'), t('loading.pollFailed', { message: errorMessage(err) }), true);
    },
    [showToast, t]
  );

  // ── Live data (initial load + polling + threat assessment) ───────────────────
  const {
    sites,
    setSites,
    perimeters,
    setPerimeters,
    threatProfiles,
    setThreatProfiles,
    protectedUsers,
    suspects,
    setSuspects,
    positions,
    loading: liveLoading,
  } = useLiveData({
    enabled: loggedIn && !connectionError,
    settings,
    notHostileIds,
    onEvent: handleThreatEvent,
    onInitialLoad: handleInitialLoad,
    onError: handleLoadError,
    onPoll: handlePoll,
    onPollError: handlePollError,
    // Switching identity (impersonation) or country reloads everything.
    reloadKey: `${account?.id ?? ''}:${country ?? ''}`,
  });

  const suspectsArray = useMemo(() => Object.values<Suspect>(suspects), [suspects]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleLocClick = useCallback(
    (loc: Site) => {
      setSelectedLoc(loc);
      const locRadius = perimeters.find((p) => p.id === loc.perimeterId)?.perimeters[3].upperBound;
      setFlyTarget(fitToSites([loc], locRadius));
    },
    [perimeters]
  );

  const handleSuspectSelect = useCallback(
    (id: string) => {
      setSelectedSuspectId((prev) => (prev === id ? null : id));
      const s = suspects[id];
      if (s) setFlyTarget({ lat: positions[id]?.lat ?? s.lat, lng: positions[id]?.lng ?? s.lng, zoom: 16 });
    },
    [suspects, positions]
  );

  const handleSaveNote = useCallback(async (id: string, note: string) => {
    setSuspects((prev) => ({ ...prev, [id]: { ...prev[id], notes: note } }));
    await updateSuspect(id, { notes: note });
  }, []);

  const handleAppClick = useCallback(async (appBundle: string) => {
    setAppDetails({
      isLoading: true,
    });

    const result = await fetchAppDetails(appBundle);
    setAppDetails(result);
  }, []);

  const handleToggleMapFilter = useCallback((key: string) => {
    setMapFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleResetView = useCallback(
    (sitesOverride?: Array<Site>) => {
      setSelectedLoc(null);
      setFlyTarget(fitToSites(sitesOverride ?? sites) ?? DEFAULT_VIEW);
    },
    [sites]
  );

  const handleSaveSettings = useCallback(
    (sectionLabel?: string) => {
      const detail = sectionLabel
        ? `${sectionLabel} updated and applied to active mission`
        : 'Configuration updated and applied to active mission';
      showToast('s', 'Settings Saved', detail);
    },
    [showToast]
  );

  const handlePerimeterUpdate = useCallback(async (id: string, data: UpdatePerimeterBody) => {
    const perimeter = await updatePerimeter(id, data);
    setPerimeters((prev: Array<PerimeterConfig>) => {
      const idx = prev.findIndex((p: PerimeterConfig) => p.id === perimeter.id);
      if (idx >= 0) {
        const next = _.cloneDeep(prev);
        next[idx].perimeters = perimeter.perimeters;
        return next;
      }
      return prev;
    });
  }, []);

  const handleSaveThreatProfile = useCallback(async (data) => {
    const threatProfile = data.id.startsWith('profile')
      ? await createProfile(data)
      : await updateProfile(data.id, data);

    setThreatProfiles((prev: Array<ThreatProfile>) => {
      const idx = prev.findIndex((p: ThreatProfile) => p.id === threatProfile.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...threatProfile };
        return next;
      }
      return [...prev, { ...threatProfile, count: 0 }];
    });
  }, []);

  const handleCreateZone = useCallback(
    async (payload: NewZonePayload) => {
      // Without a loaded perimeter set there is nothing to attach the zone to; the modal shows the message.
      const config = perimeters[0];
      if (!config) throw new Error(t('addZone.noPerimeters'));
      const site = await createSite({
        name: payload.name,
        type: payload.type,
        address: payload.address,
        lat: payload.lat,
        lng: payload.lng,
        perimeterId: config.id,
        radius: config.perimeters[3].upperBound,
      });
      setSites((prev) => [...prev, site]);
      setFlyTarget({ lat: site.lat, lng: site.lng, zoom: 16 });
      showToast('s', 'Protected Zone Added', `${site.name} now under monitoring`);
    },
    [showToast, perimeters, t]
  );

  const handleDeleteSite = useCallback(
    async (site: Site) => {
      try {
        await deleteSite(site.id);
      } catch (err) {
        showToast('e', t('suspectPanel.deleteSiteFailed'), (err as Error)?.message ?? '');
        return;
      }
      setSites((prev) => prev.filter((s) => s.id !== site.id));
      setSelectedLoc((prev) => (prev?.id === site.id ? null : prev));
      showToast('s', t('suspectPanel.deleteSiteDone'), site.name);
    },
    [showToast, t]
  );

  /** The server detaches the profile from every perimeter set before deleting it; mirror that locally. */
  const handleDeleteThreatProfile = useCallback(async (id: string) => {
    await deleteProfile(id);
    setThreatProfiles((prev: Array<ThreatProfile>) => prev.filter((p) => p.id !== id));
    setPerimeters((prev) =>
      prev.map((config) => ({ ...config, threatProfilesIds: config.threatProfilesIds.filter((pid) => pid !== id) }))
    );
  }, []);

  /** Panel variant of the profile delete: the card has no form to show an error in, so it reports through toasts. */
  const handleDeleteProfileFromPanel = useCallback(
    async (profile: ThreatProfile) => {
      try {
        await handleDeleteThreatProfile(profile.id);
      } catch (err) {
        showToast('e', t('suspectPanel.deleteProfileFailed'), (err as Error)?.message ?? '');
        return;
      }
      showToast('s', t('suspectPanel.deleteProfileDone'), profile.name);
    },
    [handleDeleteThreatProfile, showToast, t]
  );

  /** Redefines a zone upstream; the live status and suspect count stay as they were. */
  const handleUpdateZone = useCallback(
    async (zone: Site, payload: NewZonePayload) => {
      const config = perimeters.find((p) => p.id === zone.perimeterId) ?? perimeters[0];
      if (!config) throw new Error(t('addZone.noPerimeters'));
      const site = await updateSite(zone.id, {
        name: payload.name,
        type: payload.type,
        address: payload.address,
        lat: payload.lat,
        lng: payload.lng,
        perimeterId: config.id,
        radius: config.perimeters[3].upperBound,
      });
      const merge = (prev: Site) => ({ ...prev, ...site, status: prev.status, suspects: prev.suspects });
      setSites((prev) => prev.map((s) => (s.id === site.id ? merge(s) : s)));
      setSelectedLoc((prev) => (prev?.id === site.id ? merge(prev) : prev));
      showToast('s', t('addZone.updated'), site.name);
    },
    [perimeters, showToast, t]
  );

  const closeZoneModal = useCallback(() => setZoneModal({ open: false }), []);
  const closeProfileModal = useCallback(() => setProfileModal({ open: false }), []);

  const handleNonHostile = useCallback(async () => {
    if (!breach) return;
    const id: string = breach.suspect.id;

    setBreach(null);
    setNotHostileIds((prev) => [...prev, id]);
    setSuspects((prev) => _.omit(prev, id));
    showToast('s', 'MARKED NOT HOSTILE', `${id} flagged as non-threat — monitoring reduced`);
    await updateSuspect(id, { notHostile: true });
  }, [breach, showToast, setSuspects]);

  const handleLogout = useCallback(() => {
    setSettingsOpen(false);
    setTimeout(() => {
      void endSession();
      setSettings(null);
      setAlerts([]);
      setTotalAlerts(0);
      setTickerItems([]);
      setToasts([]);
      setBreach(null);
      setSelectedSuspectId(null);
      setSelectedLoc(null);
    }, 350);
  }, [endSession, setSettings]);

  // ── Computed values ───────────────────────────────────────────────────────────
  const proximity = Object.values<Position>(positions).filter((p) => p.perimeterIdx !== undefined && p.perimeterIdx <= 2).length;
  const selectedSuspect = selectedSuspectId ? suspects[selectedSuspectId] : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  const renderPerimeters = () => {
    if (!perimsOn) return null;

    const showPerimeterLabels = settings?.general.showPerimeterLabels;

    // A site whose perimeter set is unknown (e.g. the perimeters failed to load) gets no rings.
    if (selectedLoc) {
      const config = perimeters.find((p) => p.id === selectedLoc.perimeterId);
      if (!config) return null;
      return config.perimeters.map((p, idx) => {
          const { color, weight, dashArray, fillOpacity } = PERIM_CONFIG[idx];

          const radius = p.upperBound;
          const topLat = selectedLoc.lat + radius / 111320;

          return (
            <React.Fragment key={`${selectedLoc.id}_${p.upperBound}`}>
              <Circle
                center={[selectedLoc.lat, selectedLoc.lng]}
                radius={radius}
                pathOptions={{
                  color: color,
                  weight: weight,
                  dashArray: dashArray,
                  fill: true,
                  fillColor: color,
                  fillOpacity: fillOpacity,
                  interactive: false,
                }}
              />
              <PerimeterLabel
                center={[topLat, selectedLoc.lng]}
                radius={radius}
                color={color}
                show={showPerimeterLabels}
              />
            </React.Fragment>
          );
        });
    }

    return sites.map((site) => {
      const config = perimeters.find((p) => p.id === site.perimeterId);
      if (!config) return null;
      const radius = config.perimeters[3].upperBound;
      const { color } = PERIM_CONFIG[PERIM_CONFIG.length - 1];

      const topLat = site.lat + radius / 111320;

      return (
        <React.Fragment key={`${site.id}_${radius}`}>
          <Circle
            center={[site.lat, site.lng]}
            radius={radius}
            pathOptions={{
              color: color,
              weight: 1,
              dashArray: '3 5',
              fill: true,
              fillColor: color,
              fillOpacity: 0.02,
              interactive: false,
            }}
          />
          <PerimeterLabel center={[topLat, site.lng]} radius={radius} color={color} show={showPerimeterLabels} />
        </React.Fragment>
      );
    });
  };

  if (sessionStatus === 'booting' || (loggedIn && !settings && !connectionError)) {
    return <Spinner size={28} />;
  }

  const connectionBanner = connectionError && (
    <div className="conn-error">
      <span className="conn-error-icon">⚠️</span>
      <div>
        <div className="conn-error-text">API Server Disconnected</div>
        <div className="conn-error-sub">{connectionError} — running with local data (no live updates)</div>
      </div>
      <button
        className="conn-error-retry"
        onClick={() => {
          // Clearing the error re-enables useLiveData, which reruns the initial load.
          setSettings(null);
          setConnectionError(null);
        }}
      >
        RETRY
      </button>
      <button className="conn-error-dismiss" onClick={() => setConnectionError(null)}>
        ✕
      </button>
    </div>
  );

  // Settings never arrived: the shell cannot render without them, so show only the banner (with RETRY).
  if (loggedIn && !settings) {
    return (
      <>
        {connectionBanner}
        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </>
    );
  }

  return (
    <>
      {connectionBanner}

      {!loggedIn && <LoginScreen />}

      <AddZoneModal
        key={`zone-${zoneModal.zone?.id ?? 'new'}`}
        open={zoneModal.open}
        zone={zoneModal.zone}
        onClose={closeZoneModal}
        onSubmit={zoneModal.zone ? (payload) => handleUpdateZone(zoneModal.zone!, payload) : handleCreateZone}
      />
      <ThreatProfileModal
        key={`profile-${profileModal.profile?.id ?? 'new'}`}
        open={profileModal.open}
        profile={profileModal.profile}
        onClose={closeProfileModal}
        onSave={async (data) => {
          await handleSaveThreatProfile(data);
          closeProfileModal();
        }}
      />

      <SettingsScreen
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onLogout={handleLogout}
        onSave={handleSaveSettings}
        suspects={suspects}
        perimeters={perimeters}
        onPerimeterUpdate={handlePerimeterUpdate}
      />

      {loggedIn && liveLoading && (
        <div className="loading-veil" role="status" aria-live="polite">
          <div className="loading-veil-spinner" />
          <div>{t('loading.account', { name: account.username })}</div>
        </div>
      )}

      <div className="shield-shell">
        <Nav
          totalAlerts={totalAlerts}
          onOpenSettings={() => setSettingsOpen(true)}
          activeView={activeView}
          onChangeView={setActiveView}
          account={account}
          impersonatedBy={impersonatedBy}
          onStopImpersonation={stopImpersonation}
          editMode={editMode}
          onEditModeChange={setEditMode}
          country={country}
          onChangeCountry={setCountry}
        />

        <div className="workspace">
          {activeView !== 'map' && (
            <div className="view-placeholder">
              <div className="vp-icon">
                {activeView === 'graph' && '🔗'}
                {activeView === 'timeline' && '⏱'}
                {activeView === 'analysis' && '📊'}
              </div>
              <div className="vp-title">
                {activeView === 'graph' && 'Social Graph View'}
                {activeView === 'timeline' && 'Timeline View'}
                {activeView === 'analysis' && 'Analysis View'}
              </div>
              <div className="vp-sub">Coming Soon</div>
            </div>
          )}

          {activeView === 'map' && (
            <>
              {/* LEFT — Protected Zones + Threat Profiles */}
              <SuspectPanel
                protectedSites={sites}
                threatProfiles={threatProfiles}
                selectedLocId={selectedLoc?.id}
                onLocClick={handleLocClick}
                onAddSite={canManageZones ? () => setZoneModal({ open: true }) : undefined}
                onEditSite={canManageZones ? (loc) => setZoneModal({ open: true, zone: loc }) : undefined}
                onDeleteSite={canManageZones ? handleDeleteSite : undefined}
                onAddProfile={canManageProfiles ? () => setProfileModal({ open: true }) : undefined}
                onEditProfile={canManageProfiles ? (p) => setProfileModal({ open: true, profile: p }) : undefined}
                onDeleteProfile={canManageProfiles ? handleDeleteProfileFromPanel : undefined}
              />

              {/* CENTER — Map */}
              <div className="map-section">
                <MapContainer
                  center={
                    mapHome
                      ? [mapHome.lat, mapHome.lng]
                      : settings?.mission.mapCenterLat
                        ? [settings.mission.mapCenterLat, settings.mission.mapCenterLng]
                        : [0, 0]
                  }
                  zoom={mapHome?.zoom ?? settings?.general.mapDefaultZoom ?? 15}
                  zoomControl={false}
                  style={{ width: '100%', height: '100%' }}
                >
                  <BaseMapLayer />
                  <MapController flyTarget={flyTarget} />

                  {/* Protected location markers */}
                  {!!mapFilters.protected &&
                    sites.map((loc) => (
                      <Marker
                        key={loc.id}
                        position={[loc.lat, loc.lng]}
                        icon={makeLocationIcon(loc)}
                        zIndexOffset={200}
                        eventHandlers={{ click: () => handleLocClick(loc) }}
                      />
                    ))}

                  {/* Perimeter rings around selected location or the outmost ring over every location if none are selected */}
                  {renderPerimeters()}

                  {/* Suspect trails + markers */}
                  {Object.entries<Position>(positions).map(([id, pos]) => {
                    const s = suspects[id];
                    if (!s || (!mapFilters.highThreat && s.level === 'h') || (!mapFilters.flagged && s.level !== 'h')) {
                      return null;
                    }

                    // const near = (pos.nearDist ?? 9999) < 200 && s.level === 'h'
                    const near = pos.status === 'breach';

                    return (
                      <span key={id}>
                        {!!mapFilters.trails && !!settings?.general.showTrails && pos.trail.length > 1 && (
                          <Polyline
                            positions={pos.trail}
                            pathOptions={{
                              color: s.level === 'h' ? '#e63946' : s.level === 'm' ? '#f4a261' : '#4cc9f0',
                              weight: 1.5,
                              opacity: 0.45,
                              dashArray: '4 3',
                            }}
                          />
                        )}
                        <Marker
                          position={[pos.lat, pos.lng]}
                          icon={makeSuspectIcon({ ...s, _near: near })}
                          zIndexOffset={300}
                          eventHandlers={{ click: () => handleSuspectSelect(id) }}
                        />
                      </span>
                    );
                  })}

                  {/* Protected user markers */}
                  {!!mapFilters.protectedUsers &&
                    protectedUsers.map((u) => (
                      <Marker
                        key={u.objectId}
                        position={[u.lat, u.lng]}
                        icon={makeProtectedUserIcon(u)}
                        zIndexOffset={250}
                      />
                    ))}

                  {/* Map control buttons */}
                  <MapControls
                    onResetView={() => handleResetView()}
                    perimsOn={perimsOn}
                    onTogglePerims={() => setPerimsOn((p) => !p)}
                  />
                </MapContainer>

                {/* Map filter chips */}
                <MapFilters
                  filters={mapFilters}
                  onToggleFilter={handleToggleMapFilter}
                  showTrails={settings?.general.showTrails ?? false}
                />

                {/* Map stats bar */}
                <MapStats
                  suspectCount={Object.keys(suspects).length}
                  proximity={proximity}
                  siteCount={sites.length}
                  clock={lastUpdate}
                />

                <IntelTicker tickerItems={tickerItems} />
              </div>

              {/* RIGHT — Alerts + Social Graph */}
              <AlertPanel alerts={alerts} suspects={suspects} onSelectSuspect={handleSuspectSelect} />

              {/* BOTTOM PANEL — Watchlist */}
              <WatchlistPanel
                suspectsArray={suspectsArray}
                positions={positions}
                selectedId={selectedSuspectId}
                onSelect={handleSuspectSelect}
                onFlyTo={setFlyTarget}
                protectedUsers={protectedUsers}
              />
            </>
          )}
        </div>
      </div>

      {/* Suspect profile side drawer */}
      <SuspectProfile
        suspect={selectedSuspect}
        pos={selectedSuspectId ? positions[selectedSuspectId] : null}
        onClose={() => setSelectedSuspectId(null)}
        onSaveNote={handleSaveNote}
        readOnly={!canAnnotate}
        onAppClick={handleAppClick}
        suspectsData={suspects}
        alerts={selectedSuspectId ? alerts.filter((a) => a.sus === selectedSuspectId) : []}
      />

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Breach overlay */}
      <BreachOverlay
        breach={breach}
        onAck={() => {
          setBreach(null);
          showToast('w', 'BREACH ACKNOWLEDGED', 'Suspect tracked — increased monitoring active');
        }}
        onDismiss={() => setBreach(null)}
        onNotHostile={() => {
          handleNonHostile();
        }}
        canMarkNotHostile={canAnnotate}
      />

      {/* App details modal */}
      <AppDetailsModal app={appDetails} onDismiss={() => setAppDetails(null)} />

      {/* Breach screen flash effect */}
      {breach && <div className="breach-flash" key={breach.suspect.id} />}
    </>
  );
}
